<template>
    <section
        id="features"
        class="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8"
        aria-labelledby="features-heading"
    >
        <div class="max-w-2xl">
            <h2
                id="features-heading"
                class="text-3xl font-bold tracking-tight"
            >
                Everything you need to look professional and get booked
            </h2>
            <p class="mt-3 text-slate-600">
                TinyCal gives solo experts the essentials: clear services, a
                polished booking flow, reminders, intake, and payments without a
                messy tool stack.
            </p>
        </div>

        <ul class="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <li
                v-for="feature in features"
                :key="feature.title"
                class="contents"
            >
                <article
                    class="rounded-xl border border-slate-200 bg-white p-6 shadow-xs shadow-slate-200/70"
                    :aria-labelledby="feature.headingId"
                >
                    <div class="flex items-start justify-between gap-3">
                        <div
                            class="flex h-12 w-12 items-center justify-center rounded-xl"
                            :class="feature.iconContainerClass"
                            aria-hidden="true"
                        >
                            <LandingPageIcon
                                :name="feature.iconName"
                                class="h-6 w-6"
                            />
                        </div>

                        <p
                            v-if="feature.badgeText !== undefined"
                            class="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900"
                        >
                            {{ feature.badgeText }}
                        </p>
                    </div>

                    <h3
                        :id="feature.headingId"
                        class="mt-4 text-lg font-bold"
                    >
                        {{ feature.title }}
                    </h3>
                    <p class="mt-2 text-slate-600">{{ feature.description }}</p>
                </article>
            </li>
        </ul>
    </section>
</template>

<script setup lang="ts">
    import LandingPageIcon from '@/Marketing/LandingPage/Component/LandingPageIcon.vue'
    import type { LandingPageIconName } from '../LandingPageIcon.vue'

    interface Feature {
        title: string
        description: string
        iconContainerClass: string
        iconName: LandingPageIconName
        headingId: string
        badgeText?: string
    }

    const features: Feature[] = [
        {
            title: 'Group meetings',
            description:
                'Run workshops, office hours, and cohort sessions with attendee limits per time slot.',
            iconContainerClass: 'bg-purple-50 text-purple-700',
            iconName: 'group',
            headingId: 'feature-group-meetings',
        },
        {
            title: 'Recurring meetings',
            description:
                'Offer ongoing coaching or consulting sessions on a steady cadence without rebuilding bookings each time.',
            iconContainerClass: 'bg-primary-shade-ligth text-primary-shade',
            iconName: 'recurring',
            headingId: 'feature-recurring-meetings',
        },
        {
            title: 'Payment ready',
            description:
                'Collect payment before a session through Stripe so free calls and paid offers can live in one system.',
            iconContainerClass: 'bg-emerald-50 text-emerald-700',
            iconName: 'dollar-sign',
            headingId: 'feature-payment-ready',
        },
        {
            title: 'Automated reminders',
            description:
                'Email reminders reduce no-shows and help clients show up prepared.',
            iconContainerClass: 'bg-sky-50 text-sky-700',
            iconName: 'clock',
            headingId: 'feature-automated-reminders',
        },
        {
            title: 'Bookable service pages',
            description:
                'Present discovery calls, strategy sessions, and paid offers with a clean page you can share anywhere.',
            iconContainerClass: 'bg-primary-shade-ligth text-primary-shade',
            iconName: 'user-plus',
            headingId: 'feature-bookable-service-pages',
        },
        {
            title: 'Intake forms',
            description:
                'Collect goals, background, budget, and intake details before the meeting starts.',
            iconContainerClass: 'bg-amber-50 text-amber-700',
            iconName: 'file-text',
            headingId: 'feature-intake-forms',
        },
        {
            title: 'No overlaps',
            description:
                'Calendar sync and availability rules keep your schedule realistic and prevent double bookings automatically.',
            iconContainerClass: 'bg-primary-shade-ligth text-primary-shade',
            iconName: 'calendar',
            headingId: 'feature-no-overlaps',
        },
        {
            title: 'Automations',
            description:
                'Run trigger-based workflows for follow-ups and operational tasks as your solo business grows.',
            iconContainerClass: 'bg-amber-50 text-amber-700',
            iconName: 'link',
            headingId: 'feature-automations',
            badgeText: 'Upcoming',
        },
        {
            title: 'SMS and WhatsApp reminders',
            description:
                'Send reminder nudges through SMS and WhatsApp in addition to email for higher-visibility follow-up.',
            iconContainerClass: 'bg-cyan-50 text-cyan-700',
            iconName: 'message-square',
            headingId: 'feature-sms-whatsapp-reminders',
            badgeText: 'Upcoming',
        },
    ]
</script>
